/**
 * i18next configuration for the ETT SPA.
 *
 * Two locales bundled at build time: `en` (fallback) and `fr`. The detector
 * order picks the first hit from: explicit ?lng URL param → localStorage →
 * navigator language. The user choice persists in `ett.lng` localStorage so
 * subsequent visits reuse it without a flash of English.
 *
 * Namespaces map roughly to surface areas:
 *   - common   — buttons, status words, language picker
 *   - auth     — login + redirects + 403/404 stubs
 *   - app      — front-office (Chat, Map, Portal)
 *   - studio   — back-office (Studio shell + all sub-pages)
 *   - errors   — API error codes → user-facing message
 *
 * When you add a new key, mirror it in BOTH `en/<ns>.json` and `fr/<ns>.json`.
 * Missing French translations fall back to English at runtime (so a half-done
 * extraction won't crash anything) but the gap should be filled before merge.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import appEn from './locales/en/app.json';
import authEn from './locales/en/auth.json';
import commonEn from './locales/en/common.json';
import errorsEn from './locales/en/errors.json';
import helpEn from './locales/en/help.json';
import studioEn from './locales/en/studio.json';
import appFr from './locales/fr/app.json';
import authFr from './locales/fr/auth.json';
import commonFr from './locales/fr/common.json';
import errorsFr from './locales/fr/errors.json';
import helpFr from './locales/fr/help.json';
import studioFr from './locales/fr/studio.json';

export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LOCAL_STORAGE_KEY = 'ett.lng';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    defaultNS: 'common',
    ns: ['common', 'auth', 'app', 'studio', 'errors', 'help'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: LOCAL_STORAGE_KEY,
      caches: ['localStorage'],
    },
    resources: {
      en: {
        common: commonEn,
        auth: authEn,
        app: appEn,
        studio: studioEn,
        errors: errorsEn,
        help: helpEn,
      },
      fr: {
        common: commonFr,
        auth: authFr,
        app: appFr,
        studio: studioFr,
        errors: errorsFr,
        help: helpFr,
      },
    },
  });

export default i18n;
